import { useCallback, useEffect, useState } from 'react'

import { api, ErrorApi, type Cliente, type EntradaCotizacion, type EntradaIndustrial, type EntradaOffgrid, type ResumenCotizacion } from '../api'
import { fechaCorta, num, pct, usd } from '../formato'
import { AgregarIdentificacion } from './AgregarIdentificacion'
import { CompararVersiones } from './CompararVersiones'
import { DetalleCotizacion } from './DetalleCotizacion'
import { CLASE_ESTADO, ETIQUETA_ESTADO } from './EstadoVersion'
import { HistorialConsumo } from './HistorialConsumo'
import { NuevaCotizacion } from './NuevaCotizacion'
import { NuevaCotizacionIndustrial } from './NuevaCotizacionIndustrial'
import { NuevaCotizacionOffgrid } from './NuevaCotizacionOffgrid'

const ORIGEN: Record<Cliente['origen'], string> = {
  panel: 'Creado en el panel',
  web: 'Llegó por el formulario web',
  whatsapp: 'Llegó por WhatsApp',
}

type Modo =
  | { tipo: 'ficha' }
  | { tipo: 'nueva'; base?: EntradaCotizacion }
  | { tipo: 'nueva-industrial'; base?: EntradaIndustrial }
  | { tipo: 'nueva-offgrid'; base?: EntradaOffgrid }
  | { tipo: 'detalle'; id: string }
  | { tipo: 'comparar'; ids: [string, string] }

const sistema = (c: ResumenCotizacion) =>
  c.tipo === 'offgrid'
    ? c.potencia_kwp > 0
      ? `${num(c.potencia_kwp)} kWp · Off-grid`
      : 'Respaldo sin paneles'
    : `${num(c.potencia_kwp)} kWp · ${c.tipo === 'industrial' ? 'Industrial trifásico' : c.con_bateria ? 'Híbrido' : 'On-grid'}`

export function FichaCliente({ clienteId, alVolver }: { clienteId: string; alVolver: () => void }) {
  const [cliente, setCliente] = useState<Cliente | null>(null)
  const [cotizaciones, setCotizaciones] = useState<ResumenCotizacion[]>([])
  const [modo, setModo] = useState<Modo>({ tipo: 'ficha' })
  const [marcadas, setMarcadas] = useState<string[]>([])
  const [error, setError] = useState('')

  const cargar = useCallback(async () => {
    try {
      const [{ cliente }, { cotizaciones }] = await Promise.all([api.cliente(clienteId), api.cotizaciones(clienteId)])
      setCliente(cliente)
      setCotizaciones(cotizaciones)
    } catch (err) {
      setError(err instanceof ErrorApi ? err.message : 'No se pudo cargar.')
    }
  }, [clienteId])

  useEffect(() => {
    void cargar()
  }, [cargar])

  if (modo.tipo === 'nueva') {
    return (
      <NuevaCotizacion
        clienteId={clienteId}
        base={modo.base}
        categoriaCliente={cliente?.categoria}
        alElegirOffgrid={() => setModo({ tipo: 'nueva-offgrid' })}
        alCancelar={() => setModo({ tipo: 'ficha' })}
        alGuardar={(id) => {
          void cargar()
          setModo({ tipo: 'detalle', id })
        }}
      />
    )
  }
  if (modo.tipo === 'nueva-industrial') {
    return (
      <NuevaCotizacionIndustrial
        clienteId={clienteId}
        base={modo.base}
        alCancelar={() => setModo({ tipo: 'ficha' })}
        alGuardar={(id) => {
          void cargar()
          setModo({ tipo: 'detalle', id })
        }}
      />
    )
  }
  if (modo.tipo === 'nueva-offgrid') {
    return (
      <NuevaCotizacionOffgrid
        clienteId={clienteId}
        base={modo.base}
        alCancelar={() => setModo({ tipo: 'ficha' })}
        alElegirEstandar={() => setModo({ tipo: 'nueva' })}
        alGuardar={(id) => {
          void cargar()
          setModo({ tipo: 'detalle', id })
        }}
      />
    )
  }
  if (modo.tipo === 'detalle') {
    return (
      <DetalleCotizacion
        cotizacionId={modo.id}
        alVolver={() => {
          void cargar()
          setModo({ tipo: 'ficha' })
        }}
        alNuevaVersion={(c) =>
          setModo(
            c.tipo === 'offgrid'
              ? { tipo: 'nueva-offgrid', base: c.entrada as EntradaOffgrid }
              : c.tipo === 'industrial'
                ? { tipo: 'nueva-industrial', base: c.entrada as EntradaIndustrial }
                : { tipo: 'nueva', base: c.entrada as EntradaCotizacion },
          )
        }
      />
    )
  }

  if (modo.tipo === 'comparar') {
    return <CompararVersiones ids={modo.ids} alVolver={() => setModo({ tipo: 'ficha' })} />
  }

  // Como mucho dos marcadas: al marcar una tercera se suelta la mas antigua.
  const marcar = (id: string) =>
    setMarcadas((actuales) => (actuales.includes(id) ? actuales.filter((m) => m !== id) : [...actuales, id].slice(-2)))

  return (
    <section>
      <button className="enlace volver" onClick={alVolver}>
        ← Volver a la búsqueda
      </button>
      {error && <p className="error">{error}</p>}
      {cliente && (
        <>
          <div className="encabezado">
            <div>
              <h1>{cliente.nombre}</h1>
              <p className="suave">
                <span className={`insignia insignia-${cliente.categoria}`}>{cliente.categoria}</span> ·{' '}
                {ORIGEN[cliente.origen]}
              </p>
            </div>
            <div className="botones">
              <button className={cliente.categoria === 'residencial' ? 'primario' : 'secundario'} onClick={() => setModo({ tipo: 'nueva' })}>
                + Cotización estándar
              </button>
              <button className={cliente.categoria === 'residencial' ? 'secundario' : 'primario'} onClick={() => setModo({ tipo: 'nueva-industrial' })}>
                + Cotización industrial
              </button>
              <button className="secundario" onClick={() => setModo({ tipo: 'nueva-offgrid' })}>
                + Cotización off-grid
              </button>
            </div>
          </div>

          <div className="tarjeta datos">
            <dl>
              <dt>{cliente.tipo_identificacion === 'ruc' ? 'RUC' : 'Cédula'}</dt>
              <dd>
                {cliente.identificacion ?? (
                  <AgregarIdentificacion clienteId={cliente.id} alGuardar={setCliente} />
                )}
              </dd>
              <dt>Teléfono</dt>
              <dd>{cliente.telefono || <span className="suave">—</span>}</dd>
              <dt>Correo</dt>
              <dd>{cliente.correo || <span className="suave">—</span>}</dd>
              <dt>Cliente desde</dt>
              <dd>{new Date(cliente.created_at).toLocaleDateString('es-EC')}</dd>
            </dl>
          </div>

          <h2>Historial de consumo</h2>
          <HistorialConsumo clienteId={cliente.id} conDemanda={cliente.categoria !== 'residencial'} />

          <div className="titulo-con-accion">
            <h2>Cotizaciones</h2>
            {cotizaciones.length > 1 && (
              <button
                className="secundario pequeno"
                disabled={marcadas.length !== 2}
                onClick={() => setModo({ tipo: 'comparar', ids: [marcadas[0]!, marcadas[1]!] })}
              >
                {marcadas.length === 2 ? 'Comparar las dos marcadas' : 'Marca dos versiones para compararlas'}
              </button>
            )}
          </div>
          {cotizaciones.length === 0 ? (
            <div className="tarjeta pendiente vacio">
              <p>Todavía no tiene cotizaciones.</p>
              <div className="botones">
                <button className="primario" onClick={() => setModo({ tipo: cliente.categoria === 'residencial' ? 'nueva' : 'nueva-industrial' })}>
                  Crear la primera
                </button>
              </div>
            </div>
          ) : (
            <div className="tabla-envoltura">
              <table className="tabla">
                <thead>
                  <tr>
                    <th aria-label="Comparar" />
                    <th>Versión</th>
                    <th>Sistema</th>
                    <th className="num">Total</th>
                    <th className="num">VAN</th>
                    <th className="num">TIR</th>
                    <th>Estado</th>
                    <th>Nota</th>
                    <th>Fecha</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {cotizaciones.map((c) => (
                    <tr key={c.id} onClick={() => setModo({ tipo: 'detalle', id: c.id })}>
                      <td onClick={(e) => e.stopPropagation()}>
                        <input type="checkbox" aria-label={`Comparar v${c.version}`} checked={marcadas.includes(c.id)} onChange={() => marcar(c.id)} />
                      </td>
                      <td className="fuerte">v{c.version}</td>
                      <td>{sistema(c)}</td>
                      <td className="num">{usd(c.total)}</td>
                      {c.van === null ? (
                        <td className="num suave">—</td>
                      ) : (
                        <td className={c.van >= 0 ? 'num positivo' : 'num negativo'}>{usd(c.van)}</td>
                      )}
                      <td className="num">{pct(c.tir)}</td>
                      <td>
                        <span className={`insignia ${CLASE_ESTADO[c.estado]}`}>{ETIQUETA_ESTADO[c.estado]}</span>
                      </td>
                      <td className="suave">{c.nota || '—'}</td>
                      <td className="suave">
                        {fechaCorta(c.created_at)}
                        <small className="bloque">{c.creado_por_nombre}</small>
                      </td>
                      <td className="accion">Abrir →</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </section>
  )
}
